// Light-DOM stylesheets + shadow-root template for dialog.js's two
// variants (modal + corner). Kept as a sibling module so dialog.js
// stays focused on lifecycle / wiring; the visual layer lives here.
//
// The css / html tags are String.raw aliases — runtime no-ops that
// trigger editor syntax highlighting (vscode-styled-components etc.)
// on tagged template literals. The interpolated values (TRANSITION,
// SPINNER_SVG) are inlined as strings at module-load time.

import { faSpinner } from '@fortawesome/free-solid-svg-icons'

const css = String.raw
const html = String.raw

// Single source of truth for the fade-in/out timing. dialog.js
// imports FADE_DURATION for the closeDialog teardown setTimeout so
// the JS unmount lines up with the CSS transition end.
export const FADE_DURATION = 300
const TRANSITION = `${FADE_DURATION}ms ease-out`

export const takeoverStyles = css`
  .plg-dialog-takeover {
    position: fixed !important;
    inset: 0;
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    margin: 0;
    padding: 0;
    border: none;
    background: transparent;
    overflow: hidden;
  }
  /* Stripe's 3DS challenge brings its own backdrop, so ours stays
     invisible — but NOT fully transparent: at 0 opacity some browsers
     let clicks fall through the backdrop. The symbolic 1% keeps it
     hit-testing as a solid layer. Don't "tidy" this to 0. */
  .plg-dialog-takeover::backdrop,
  .plg-dialog-takeover + .backdrop {
    background: rgba(0, 0, 0, 0.01);
  }
  .plg-dialog-takeover iframe {
    display: block !important;
    width: 100% !important;
    height: 100% !important;
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }
`

// Light-DOM stylesheet for the **modal** variant — only the dialog
// outer + backdrop + body host element box. Anything visible *inside*
// the body (close button, spinner) lives in the shadow root below
// and is isolated from partner CSS.
//
// Breakpoints + dimensions ported from the v7 widget's .widget-modal
// (app/javascript/styles/tailwind/widgets/donate_v7.css on ag-v7):
//
//   * default (< 375px) — edge-to-edge fullscreen. 100dvh on
//     supporting browsers so the mobile address bar doesn't truncate;
//     100vh fallback below.
//   * ≥ 375px (iPhone SE and up) — centered card with 12px gutter,
//     border-radius, shadow. overflow: hidden clips the iframe to
//     the radius (no separate iframe rule needed).
//   * ≥ 640px — wider gutter (28px on each side), and the top tier.
//     We deliberately stop here (matching Bootstrap's flat modal
//     margin) rather than growing the gutter further on desktop — a
//     larger ≥ 768px margin was width-gated but only affected the
//     vertical gap (max-width already caps the horizontal), so it
//     squeezed landscape phones (wide + short) for no desktop payoff
//     worth the cost. The closeOutside variant keeps its own larger
//     top margin (see below) since the overhanging close button
//     needs the room.
export const modalStyles = css`
  .plg-dialog {
    position: fixed;
    inset: 0;
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    margin: 0;
    padding: 0;
    border: none;
    background: transparent;
    overflow-y: auto;
    overflow-x: hidden;
  }
  /* Lock the HOST PAGE's scroll while a modal dialog is open. Declared
     in CSS (not by JS toggling documentElement.style.overflow) so it
     composes with any other modal system on the same page — notably the
     FLP's own html:has(.flp-modal[open]) rule: the browser ORs the
     :has() matches, so one system closing its dialog can't release
     another's lock. Modal-only — .plg-corner-dialog (abandoned cart) is
     a separate class and stays scrollable. Uses !important because this
     is injected into partner pages we don't control, whose own
     html { overflow } could otherwise win. Safari <15.4 has no :has()
     and drops this rule — showModalDialog / closeDialog keep a JS
     fallback for that floor. */
  html:has(.plg-dialog[open]) {
    overflow: hidden !important;
  }
  /* Native browsers paint the backdrop via the ::backdrop pseudo;
     dialog-polyfill inserts a sibling .backdrop div instead. Both
     selectors target the same visual concept. */
  .plg-dialog::backdrop,
  .plg-dialog + .backdrop {
    background: rgba(0, 0, 0, 0.01);
    transition: background ${TRANSITION};
  }
  .plg-dialog.open::backdrop,
  .plg-dialog.open + .backdrop {
    background: rgba(0, 0, 0, 0.6);
  }
  /* Hides closed dialogs on browsers without native <dialog>; native
     UA stylesheet already handles this so the rule is a no-op there. */
  dialog:not([open]) { display: none; }
  /* dialog-polyfill's modal-blocking overlay div. */
  ._dialog_overlay {
    position: fixed;
    top: 0; right: 0; bottom: 0; left: 0;
  }
  /* Scope every body-level rule under .plg-dialog so it doesn't
     leak onto .plg-corner-dialog's body (both variants share the
     same .plg-dialog-body class on purpose, since the shadow-root
     contents are variant-agnostic — but the OUTER positioning /
     sizing is per-variant). */
  .plg-dialog .plg-dialog-body {
    position: relative;
    width: 100%;
    /* Edge-to-edge fullscreen on the narrowest phones (≤ iPhone SE
       width). vh fallback first for browsers without dvh; dvh
       afterwards so supporting browsers use the dynamic value. */
    min-height: 100vh;
    min-height: 100dvh;
    background: #fff;
    outline: none;
    opacity: 0;
    transform: translate(0, -50px);
    transition: opacity ${TRANSITION}, transform ${TRANSITION};
  }
  .plg-dialog.open > .plg-dialog-body {
    opacity: 1;
    transform: none;
  }
  /* "Not now" shake when a close is attempted on a locked dialog
     (donation mid-flight) via Esc or backdrop — the close button is
     disabled, so this covers the paths that can't carry a disabled
     state. dialog.js toggles .plg-dialog-locked-nudge (strip + reflow +
     re-add to replay). Keyframes start and end at translateX(0) so it
     settles back into the open state's transform: none with no jump. */
  @keyframes plg-dialog-locked-nudge {
    0%, 100% { transform: translateX(0); }
    20%, 60% { transform: translateX(-8px); }
    40%, 80% { transform: translateX(8px); }
  }
  .plg-dialog.open > .plg-dialog-body.plg-dialog-locked-nudge {
    animation: plg-dialog-locked-nudge 0.3s ease-in-out;
  }
  @media (prefers-reduced-motion: reduce) {
    .plg-dialog.open > .plg-dialog-body.plg-dialog-locked-nudge {
      animation: none;
    }
  }
  /* The slotted iframe (renderModal) or the iframe createWidget
     mounts inside a slotted widget div (openDonationModal). */
  .plg-dialog .plg-dialog-body iframe {
    display: block;
    width: 100%;
    border: 0;
  }
  /* scrollWithin variant — used by portal-mount picker modals
     (renderModal({ scrollWithin: true })). The body + iframe are
     capped at viewport height so the iframe document can scroll
     internally beneath a sticky picker header, instead of the whole
     dialog growing past the viewport like the other modals do. The
     iframe selector mirrors the bound onto the iframe child so
     resizeIframe's inline style.height growth gets clipped to the
     same value (CSS max-height overrides inline height per cascade).
     100vh fallback first, 100dvh override second — pre-Safari-15.4
     browsers ignore the dvh line and stick with the static value.
     This sub-375px rule sets the fullscreen baseline; the 375px+
     block below tightens it to viewport minus margins. */
  .plg-dialog .plg-dialog-body[data-scroll-within],
  .plg-dialog .plg-dialog-body[data-scroll-within] iframe {
    max-height: 100vh;
    max-height: 100dvh;
  }
  @media (min-width: 375px) {
    .plg-dialog .plg-dialog-body {
      --plg-dialog-margin: 12px;
      width: min(100% - var(--plg-dialog-margin) * 2, var(--plg-dialog-max-width, 640px));
      min-height: 192px;
      margin: var(--plg-dialog-margin) auto;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    }
    /* Match the body's border-radius on the iframe directly instead
       of clipping via overflow:hidden on the body — overflow would
       also clip the closeOutside variant's overhanging close button. */
    .plg-dialog .plg-dialog-body,
    .plg-dialog .plg-dialog-body iframe {
      border-radius: 12px;
    }
    /* scrollWithin at ≥ 375px — body sits inset with gutter margins
       on top + bottom, so the bound shrinks accordingly. Picks up
       --plg-dialog-margin from the cascade above (12px / 28px per
       breakpoint), so the dialog stays inscribed in the viewport
       whatever gutter the current breakpoint applies. */
    .plg-dialog .plg-dialog-body[data-scroll-within],
    .plg-dialog .plg-dialog-body[data-scroll-within] iframe {
      max-height: calc(100vh - var(--plg-dialog-margin) * 2);
      max-height: calc(100dvh - var(--plg-dialog-margin) * 2);
    }
    /* closeOutside variant — used by donate-form-in-modal where the
       widget's own header has no room for an inside close button.
       Body top margin grows to fit the overhanging close button (set
       in the shadow root below). */
    .plg-dialog .plg-dialog-body[data-close-outside] {
      margin-top: calc(var(--plg-dialog-margin) + 28px);
    }
  }
  @media (min-width: 640px) {
    .plg-dialog .plg-dialog-body {
      --plg-dialog-margin: 28px;
    }
  }
`

// Light-DOM stylesheet for the **corner** (non-modal) variant.
// Fixed-positioned in a viewport corner via the `data-position`
// attribute on the dialog. No backdrop, no fullscreen, no
// body-scroll lock. Bottom-right is the conventional notification
// corner; other corners are supported but rarely used.
//
// Two margin tiers borrowed from the modal variant: 12px on tiny
// viewports (matches the modal's first card breakpoint at 375px),
// 28px from 640px up (matches the modal's wider gutter). No further
// step-up at 768px+ — a corner dialog doesn't benefit from extra
// breathing room the way a centered card does.
export const cornerStyles = css`
  .plg-corner-dialog {
    position: fixed;
    margin: 0;
    padding: 0;
    border: none;
    background: transparent;
    overflow: visible;
    width: min(320px, calc(100% - 24px));
    max-width: none;
    max-height: none;
    /* show() (non-modal) doesn't put the dialog in the browser's
       top layer the way showModal() does, so z-index follows normal
       rules. Set high enough to clear typical partner-page chrome
       (nav bars, cookie banners, their own toasts) without going
       MAX_INT rude. */
    z-index: 2147483000;
  }
  .plg-corner-dialog[data-position="bottom-right"] { inset: auto 12px 12px auto; }
  .plg-corner-dialog[data-position="bottom-left"]  { inset: auto auto 12px 12px; }
  .plg-corner-dialog[data-position="top-right"]    { inset: 12px 12px auto auto; }
  .plg-corner-dialog[data-position="top-left"]     { inset: 12px auto auto 12px; }
  @media (min-width: 640px) {
    .plg-corner-dialog {
      width: 320px;
    }
    .plg-corner-dialog[data-position="bottom-right"] { inset: auto 28px 28px auto; }
    .plg-corner-dialog[data-position="bottom-left"]  { inset: auto auto 28px 28px; }
    .plg-corner-dialog[data-position="top-right"]    { inset: 28px 28px auto auto; }
    .plg-corner-dialog[data-position="top-left"]     { inset: 28px auto auto 28px; }
  }
  .plg-corner-dialog .plg-dialog-body {
    position: relative;
    width: 100%;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 12px 45px -6px rgba(0, 0, 0, 0.25);
    outline: none;
    opacity: 0;
    transform: translateY(20px);
    transition: opacity ${TRANSITION}, transform ${TRANSITION};
  }
  .plg-corner-dialog.open > .plg-dialog-body {
    opacity: 1;
    transform: none;
  }
  .plg-corner-dialog .plg-dialog-body iframe {
    display: block;
    width: 100%;
    border: 0;
    border-radius: 12px;
  }
`

// FA spinner SVG with the fa-spin-pulse animation, applied in CSS
// inside the shadow root below. Pulled from
// @fortawesome/free-solid-svg-icons (tree-shaken to ~200 bytes for
// just this one icon) rather than rendered via
// @fortawesome/fontawesome-svg-core's icon() runtime, which would
// add ~10kB to the embed bundle for the same visual result. Matches
// v7's `fa_icon 'solid', 'spinner', class: 'fa-spin-pulse fa-4x'`
// pattern and the legacy Bootstrap multi-org modal it replaces.
const SPINNER_SVG = html`
  <svg
    class="fa-spin-pulse"
    aria-hidden="true"
    focusable="false"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 ${faSpinner.icon[0]} ${faSpinner.icon[1]}"
    width="64"
    height="64"
  >
    <path fill="currentColor" d="${faSpinner.icon[4]}" />
  </svg>
`

// Open-shadow-DOM contents for the dialog body. Hosts the close
// button (positioned via logical CSS so RTL locales get the X on the
// inline-end side, which is the left edge), the centered loading
// spinner that the iframe paints over once it renders, and a <slot>
// for the caller's content (iframe or widget div).
//
// Shadow root is opened (mode: 'open') because: (a) the primary
// threat we're defending against is partner *CSS*, which cannot
// reach in regardless of mode; (b) partner JS that wanted to mess
// with our DOM could just nuke the dialog element from light DOM
// anyway, so closed mode buys nothing here; (c) open mode lets the
// test suite query into the shadow and lets devtools inspectors
// surface the structure during debugging.
export const shadowHTML = html`
  <style>
    .spinner {
      position: absolute;
      top: 50%;
      inset-inline-start: 50%;
      transform: translate(-50%, -50%);
      /* Sit behind the (non-positioned) iframe so the iframe paints
         over the spinner once its content loads. The body's
         stacking context (opacity / transform during the fade)
         keeps the negative z-index local to this dialog. */
      z-index: -1;
      pointer-events: none;
      /* Tailwind's text-gray-300 — what v7 uses for the same icon. */
      color: #d1d5db;
    }
    /* Mirror of FA's fa-spin-pulse keyframe; rotating the SVG in eight
       discrete steps. We can't rely on FA's stylesheet inside the
       shadow root, so the rule is inlined here. */
    @keyframes fa-spin {
      to { transform: rotate(360deg); }
    }
    .fa-spin-pulse {
      animation: fa-spin 1s infinite steps(8);
    }
    /* Default close button — inside the body, top-inline-end corner
       (right in LTR, left in RTL). z-index so it sits above whatever
       the iframe paints. Gray; no inverted white-on-dark variant. */
    .close {
      position: absolute;
      top: 8px;
      inset-inline-end: 8px;
      z-index: 10;
      width: 36px;
      height: 36px;
      background: transparent;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      font-size: 36px;
      font-weight: 200;
      color: #6b7280;
      line-height: 1;
      letter-spacing: 0;
      padding: 0;
      margin: 0;
      overflow: hidden;
      user-select: none;
      font-family: system-ui, -apple-system, "Segoe UI", Roboto,
        "Helvetica Neue", "Noto Sans", "Liberation Sans", Arial, sans-serif;
    }
    .close:not(:disabled):hover { color: #374151; }
    /* Disabled while a donation is processing (dialog locked). Dimmed +
       not-allowed cursor as the persistent "can't close now" affordance
       — the paths without a UI home (Esc / backdrop) get a shake nudge
       instead (see modalStyles). */
    .close:disabled {
      cursor: not-allowed;
      opacity: 0.45;
    }
    /* "Close" label hidden by default; only the X is visible. */
    .close-label { display: none; }

    /* closeOutside variant — used by donate-form-in-modal where the
       widget's own header has no room for an inside close button.
       At < 375px we keep the default inside-the-body close button
       (no room above when fullscreen); above that breakpoint the
       button overhangs into the backdrop area with smaller chrome
       and a visible "Close" label next to the X. */
    @media (min-width: 375px) {
      :host([data-close-outside]) .close {
        top: -28px;
        inset-inline-end: 0;
        width: auto;
        height: 28px;
        padding: 0 6px;
        font-size: 20px;
        font-weight: 300;
        color: rgb(255, 255, 255);
        align-items: baseline;
        text-shadow: 0 1px 8px rgba(0, 0, 0, 0.5);
      }
      :host([data-close-outside]) .close:not(:disabled):hover { color: rgba(255, 255, 255, 0.9); }
      :host([data-close-outside]) .close-label {
        display: inline;
        font-size: 14px;
      }
    }
  </style>
  <div class="spinner">${SPINNER_SVG}</div>
  <slot></slot>
  <button class="close" type="button" aria-label="Close">
    <span aria-hidden="true">×</span>
    <span class="close-label">Close</span>
  </button>
`
